import React from 'react';
import { Shield, Heart, Users, Code, Globe, Award, Zap, Lock } from 'lucide-react';

export const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Shield className="h-16 w-16 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">About PrivateTools</h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Empowering users with privacy-first online tools that process everything locally in your browser. 
              No uploads, no storage, no tracking - just powerful tools that respect your privacy.
            </p>
          </div>

          {/* Mission Statement */}
          <div className="mb-12">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-8 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start space-x-4">
                <Heart className="h-8 w-8 text-red-500 mt-1 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Our Mission</h2>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    We believe that powerful tools should not come at the cost of your privacy. PrivateTools was created 
                    to provide a comprehensive suite of online utilities that process all data locally in your browser, 
                    ensuring that your files, text, and personal information never leave your device.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Our Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-3 mb-4">
                  <Lock className="h-8 w-8 text-green-600 dark:text-green-400" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Privacy First</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Your data never leaves your device. All processing happens locally in your browser, 
                  ensuring complete privacy and security.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-3 mb-4">
                  <Zap className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Fast & Efficient</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  No server round-trips mean instant results. Our tools are optimized for speed and 
                  provide immediate feedback.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-3 mb-4">
                  <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">User-Centric</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Built with users in mind. Simple, intuitive interfaces that make complex tasks 
                  accessible to everyone.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-3 mb-4">
                  <Code className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Open & Transparent</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  We believe in transparency. Our code is open source and our practices are clear 
                  and honest.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-3 mb-4">
                  <Globe className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Accessible</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Available to everyone, everywhere. No registration required, no barriers to entry, 
                  just tools when you need them.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-3 mb-4">
                  <Award className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Quality</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Every tool is crafted with attention to detail, ensuring reliable results and 
                  professional-grade output.
                </p>
              </div>
            </div>
          </div>

          {/* What We Offer */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">What We Offer</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">40+ Powerful Tools</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    From image processing to data conversion, encryption to text manipulation - 
                    we provide a comprehensive suite of utilities for all your needs.
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-600 dark:text-gray-400">Image Tools</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-600 dark:text-gray-400">PDF Tools</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span className="text-gray-600 dark:text-gray-400">Text Tools</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span className="text-gray-600 dark:text-gray-400">Developer Tools</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Zero Data Collection</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    We don't collect, store, or transmit any of your data. Your files and information 
                    stay on your device where they belong.
                  </p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• No user accounts or registration</li>
                    <li>• No cookies or tracking</li>
                    <li>• No analytics or data mining</li>
                    <li>• No third-party data sharing</li>
                  </ul>
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Instant Processing</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Since all processing happens in your browser, you get immediate results without 
                    waiting for server responses or dealing with upload limits.
                  </p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• No file size limits</li>
                    <li>• No upload/download delays</li>
                    <li>• Works offline</li>
                    <li>• Instant preview and results</li>
                  </ul>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Professional Quality</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Each tool is designed to produce professional-grade results suitable for business, 
                    education, and personal use.
                  </p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• High-quality output formats</li>
                    <li>• Advanced algorithms</li>
                    <li>• Error handling and validation</li>
                    <li>• Consistent results</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
                    
          {/* Why Choose Us */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Why Choose PrivateTools?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Complete Privacy</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Your data never leaves your device. No servers, no databases, no tracking.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">No Registration</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Start using tools immediately. No accounts, no passwords, no hassle.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">No Limits</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Process files of any size. No upload limits, no processing restrictions.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Instant Results</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      No waiting for uploads or server processing. Get results immediately.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Always Available</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Works offline, no internet required after initial load. Access tools anytime.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Professional Quality</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Enterprise-grade tools with professional output quality and reliability.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Our Commitment */}
          <div className="mb-12">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-8 border border-green-200 dark:border-green-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">Our Commitment to You</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Privacy Protection</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    We will never collect, store, or share your personal data. Your privacy is our top priority.
                  </p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Code className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Continuous Improvement</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    We continuously improve our tools based on user feedback and technological advancements.
                  </p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">User-First Design</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Every feature and improvement is designed with our users' needs and feedback in mind.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="text-center">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
                Thank you for choosing PrivateTools. We're committed to providing you with the best 
                privacy-first online tools experience.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                © 2025 PrivateTools. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 